import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { User } from '../../../core/models/user.interface';
import { SUBSCRIPTION_TYPES } from '../../../core/shared/constants';

declare var JitsiMeetExternalAPI: any;

interface AppointmentData {
  _id: string;
  date: string;
  time: string;
  dateValue: string;
  customer: {
    id: string;
    name: string;
  };
  coach: {
    id: string;
    name: string;
    type: {
      id: number;
      name: string;
    };
  };
  meetUrl?: string;
  createdAt: string;
}

@Component({
  selector: 'app-meet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './meet.component.html',
  styleUrls: ['./meet.component.scss']
})
export class MeetComponent implements OnInit, OnDestroy {
  private authService: AuthService = inject(AuthService);
  user: User | null = null;
  
  appointmentId: string = '';
  appointmentData: AppointmentData | null = null;
  loading: boolean = true;
  error: string = '';
  jitsiApi: any = null;
  showHeader: boolean = true; // Controla la visibilidad del header
  private mobileRecommendationsInterval: any = null; // Para controlar el intervalo de monitoreo
  
  // Exponer environment para el template
  environment = environment;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadUserData();
    // Ocultar elementos del layout principal para pantalla completa
    this.hideMainLayoutElements();
    
    this.route.params.subscribe(params => {
      this.appointmentId = params['appointmentId'];
      if (this.appointmentId) {
        this.loadAppointmentData();
      } else {
        this.error = 'ID de cita no válido';
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.jitsiApi) {
      this.jitsiApi.dispose();
    }
    // Limpiar el intervalo de monitoreo
    if (this.mobileRecommendationsInterval) {
      clearInterval(this.mobileRecommendationsInterval);
      this.mobileRecommendationsInterval = null;
    }
    // Restaurar elementos del layout principal
    this.showMainLayoutElements();
  }

  private loadJitsiScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Verificar si el script ya está cargado (desde index.html o dinámicamente)
      const existingScript = document.querySelector('script[src*="external_api.js"]');
      if (existingScript) {
        console.log('Script de Jitsi ya está cargado desde index.html');
        // Esperar un poco para asegurar que esté completamente cargado
        setTimeout(() => {
          if (typeof JitsiMeetExternalAPI !== 'undefined') {
            resolve();
          } else {
            reject(new Error('Script cargado pero API no disponible'));
          }
        }, 500);
        return;
      }

      // Cargar el script dinámicamente si no está en index.html
      console.log('Cargando script de Jitsi Meet dinámicamente...');
      const script = document.createElement('script');
      script.src = environment.jitsiMeet.apiScript;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Script de Jitsi Meet cargado exitosamente');
        resolve();
      };
      
      script.onerror = (error) => {
        console.error('Error al cargar el script de Jitsi Meet:', error);
        reject(new Error('No se pudo cargar el script de Jitsi Meet'));
      };
      
      document.head.appendChild(script);
    });
  }

  loadAppointmentData(): void {
    this.loading = true;
    this.error = '';

    // Hacer petición al backend para obtener los datos de la cita
    this.http.get<any>(`${environment.apiUrl}/appointments/${this.appointmentId}`)
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.appointmentData = response.data;
            this.loading = false;
            
            // Inicializar Jitsi Meet después de cargar los datos
            setTimeout(async () => {
              try {
                await this.initializeJitsiMeet();
              } catch (error) {
                console.error('Error al inicializar Jitsi Meet:', error);
                this.error = 'Error al cargar la aplicación de videollamada';
              }
            }, 100);
          } else {
            this.error = response.message || 'No se pudo cargar la información de la cita';
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Error al cargar datos de la cita:', error);
          this.error = 'No se pudo cargar la información de la cita';
          this.loading = false;
        }
      });
  }

  async initializeJitsiMeet(): Promise<void> {
    if (!this.appointmentData) {
      console.error('No hay datos de cita disponibles');
      return;
    }

    // Cargar el script de Jitsi Meet si no está disponible
    if (typeof JitsiMeetExternalAPI === 'undefined') {
      console.log('Cargando script de Jitsi Meet...');
      await this.loadJitsiScript();
    }

    // Verificar nuevamente que JitsiMeetExternalAPI esté disponible
    if (typeof JitsiMeetExternalAPI === 'undefined') {
      console.error('JitsiMeetExternalAPI no está disponible después de cargar el script');
      this.error = 'Error al cargar la aplicación de videollamada';
      return;
    }

    try {
      const domain = environment.jitsiMeet.domain;
      const roomName = this.appointmentData._id || this.appointmentId;
      
      // Obtener el nombre del usuario autenticado
      const currentUser = this.user || this.authService.getUser();
      const userName = currentUser?.name || 'Usuario Phoenix';
      const userEmail = currentUser?.email || `${userName}@phoenix.com.pe`;

      const options = {
        roomName: roomName,
        parentNode: document.querySelector('#jitsi-container'),
        width: "100%",
        height: "100%",
        userInfo: {
          email: userEmail,
          displayName: userName
        },
        configOverwrite: {
          startWithAudioMuted: true,
          startWithVideoMuted: true,
          prejoinPageEnabled: false,
          enableWelcomePage: false,
          enableClosePage: false,
          enableLayerSuspension: false,
          enableNoisyMicDetection: false,
          enableTalkWhileMuted: false,
          // Configuración de branding
          defaultLogoUrl: 'https://apptest.phoenix.com.pe/assets/img/logo-responsive.png',
          defaultLocalDisplayName: 'Participante Phoenix',
          // Configuración de tamaño del logo
          logoImageScale: 2.5,
          mainLogoImageScale: 2.5,
          watermarkImageScale: 2.5,
          // Configuraciones adicionales para tamaño del logo
          logoWidth: 200,
          logoHeight: 60,
          mainLogoWidth: 200,
          mainLogoHeight: 60,
          // Ocultar opciones del menú
          disableModeratorIndicator: true,
          disablePolls: true,
          disablePrivateMessages: false,
          disableProfile: false,
          disableRecordAudioNotification: false,
          enableRecordAudioNotification: true,
          disableRemoteMute: false,
          disableThirdPartyRequests: true,
          disableInviteFunctions: false,
          disableVideoBackground: false,
          disableVideoBlur: false,
          disableVideoBackgroundBlur: false,
          disableRecording: false,
          enableRecording: true,
          disableLiveStreaming: false,
          disableEmbedMeeting: true,
          disableSharedVideo: true,
          disableSharedAudio: true,
          disableBackgroundEffects: true,
          disableNoiseSuppression: true,
          disableParticipantStats: true,
          disableAdvancedSettings: true,
          disableKeyboardShortcuts: true,
          disablePerformanceMonitoring: true,
          disableSecurityOptions: true,
          disableMoreMenu: true,
          // Deshabilitar funcionalidad de terminar reunión para todos
          disableEndMeetingForAll: true,
          disableKickParticipant: true,
          disableModeratorControls: true,
          enableEndMeetingForAll: false,
          // Configuraciones para ocultar recomendaciones móviles
          disableDeepLinking: true,
          disableAppPromotion: true,
          disableAppStorePromotion: true,
          disablePlayStorePromotion: true,
          disableAppDownloadPromotion: true,
          disableNativeAppPromotion: true,
          disableMobileAppPromotion: true,
          disableBrowserAppPromotion: true,
          // Configuraciones adicionales para simplificar
          channelLastN: 4,
          constraints: {
            video: {
              aspectRatio: 16 / 9,
              height: {
                ideal: 720,
                max: 720,
                min: 240
              }
            }
          }
        },
        interfaceConfigOverwrite: {
          // Solo mostrar botones esenciales
          TOOLBAR_BUTTONS: [
            'microphone', 'camera', 'chat', 'recording', 'hangup'
          ],
          // Ocultar secciones de configuración
          SETTINGS_SECTIONS: [],
          // Ocultar elementos de la interfaz
          SHOW_JITSI_WATERMARK: false,
          SHOW_BRAND_WATERMARK: true,
          SHOW_WATERMARK_FOR_GUESTS: true,
          SHOW_POWERED_BY: false,
          SHOW_POLICY_WATERMARK: false,
          SHOW_LEGAL_INFO: false,
          SHOW_DEEP_LINKING_PAGE: false,
          SHOW_LIVE_STREAMING_LABEL: false,
          SHOW_MEETING_TIMER: false,
          SHOW_PARTICIPANTS_IN_SIDEBAR: false,
          SHOW_CLOSE_PAGE: false,
          SHOW_FILMSTRIP_ONLY_IN_LARGE_MEETINGS: true,
          // Ocultar recomendaciones móviles
          SHOW_APP_PROMOTION: false,
          SHOW_MOBILE_APP_PROMOTION: false,
          SHOW_NATIVE_APP_PROMOTION: false,
          SHOW_BROWSER_APP_PROMOTION: false,
          SHOW_APP_STORE_PROMOTION: false,
          SHOW_PLAY_STORE_PROMOTION: false,
          SHOW_DOWNLOAD_APP_PROMOTION: false,
          // Ocultar funcionalidades de moderador
          SHOW_END_MEETING_FOR_ALL: false,
          SHOW_KICK_PARTICIPANT: false,
          SHOW_MODERATOR_CONTROLS: false,
          HIDE_END_MEETING_FOR_ALL: true,
          HIDE_KICK_PARTICIPANT: true,
          HIDE_MODERATOR_CONTROLS: true,
          // Configuración de menús
          HIDE_INVITE_MORE_HEADER: true,
          HIDE_MEETING_TIMER: true,
          HIDE_PARTICIPANTS_IN_SIDEBAR: true,
          // Ocultar opciones del menú de tres puntos
          DISABLE_DOMINANT_SPEAKER_INDICATOR: true,
          DISABLE_FOCUS_INDICATOR: true,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          DISABLE_PRESENCE_STATUS: true,
          DISABLE_RINGING: true,
          // Configuración de branding
          DEFAULT_LOGO_URL: 'https://apptest.phoenix.com.pe/assets/img/logo-responsive.png',
          PROVIDER_NAME: 'Phoenix',
          APP_NAME: 'Phoenix Meet',
          NATIVE_APP_NAME: 'Phoenix Meet',
          BRAND_WATERMARK_LINK: 'https://phoenix.com.pe',
          // Configuración de tamaño del logo
          LOGO_IMAGE_SCALE: 2.5,
          MAIN_LOGO_IMAGE_SCALE: 2.5,
          WATERMARK_IMAGE_SCALE: 2.5,
          // Configuraciones adicionales para tamaño del logo
          LOGO_WIDTH: 200,
          LOGO_HEIGHT: 60,
          MAIN_LOGO_WIDTH: 200,
          MAIN_LOGO_HEIGHT: 60,
          // Configuración adicional de logo
          LOGO_URL: 'https://apptest.phoenix.com.pe/assets/img/logo-responsive.png',
          MAIN_LOGO_URL: 'https://apptest.phoenix.com.pe/assets/img/logo-responsive.png',
          WELCOME_PAGE_LOGO_URL: 'https://apptest.phoenix.com.pe/assets/img/logo-responsive.png',
          CLOSE_PAGE_LOGO_URL: 'https://apptest.phoenix.com.pe/assets/img/logo-responsive.png',
          // Ocultar elementos adicionales
          DISABLE_VIDEO_BACKGROUND: true,
          DISABLE_VIDEO_BLUR: true,
          DISABLE_VIDEO_BACKGROUND_BLUR: true,
          // Configuración de chat simplificada
          DISABLE_CHAT: false,
          CHAT_WHITEBOARD: true,
          // Configuración de grabación
          DISABLE_RECORDING: false,
          ENABLE_RECORDING: true,
          // Configuración de invitaciones
          DISABLE_INVITE_FUNCTIONS: false,
          // Ocultar opciones de rendimiento y seguridad
          DISABLE_PERFORMANCE_MONITORING: true,
          DISABLE_SECURITY_OPTIONS: true,
          // Ocultar opciones de grabación y streaming
          DISABLE_LIVE_STREAMING: false,
          DISABLE_EMBED_MEETING: true,
          // Ocultar opciones de audio/video compartido
          DISABLE_SHARED_VIDEO: true,
          DISABLE_SHARED_AUDIO: true,
          // Ocultar opciones de fondo y efectos
          DISABLE_BACKGROUND_EFFECTS: true,
          DISABLE_NOISE_SUPPRESSION: true,
          // Ocultar estadísticas y configuraciones avanzadas
          DISABLE_PARTICIPANT_STATS: true,
          DISABLE_ADVANCED_SETTINGS: true,
          DISABLE_KEYBOARD_SHORTCUTS: true,
          // Ocultar menú de tres puntos completamente
          DISABLE_MORE_MENU: false
        }
      };

      // Crear la instancia de Jitsi Meet
      this.jitsiApi = new JitsiMeetExternalAPI(domain, options);

      // Event listeners
      this.jitsiApi.addEventListener('participantJoined', (participant: any) => {
        console.log("Se unió:", participant.displayName);
      });

      this.jitsiApi.addEventListener('participantLeft', (participant: any) => {
        console.log("Se fue:", participant.displayName);
      });

      // Evento cuando se está en la pantalla de pre-join (lobby)
      this.jitsiApi.addEventListener('prejoinPageLoaded', () => {
        console.log("Pantalla de pre-join cargada");
        // Asegurar que el header esté visible en el lobby
        this.showHeader = true;
      });

      this.jitsiApi.addEventListener('videoConferenceJoined', () => {
        console.log("Te uniste a la conferencia");
        // Ocultar el header cuando se une a la conferencia
        this.showHeader = false;
      });

      this.jitsiApi.addEventListener('videoConferenceLeft', () => {
        console.log("Saliste de la conferencia");
        // Mostrar el header nuevamente cuando sale de la conferencia
        this.showHeader = true;
        // Redirigir usando la misma lógica dinámica que el botón "Volver"
        this.redirectToUserRoute();
      });

      this.jitsiApi.addEventListener('readyToClose', () => {
        console.log("Listo para cerrar");
        this.jitsiApi.dispose();
        // Redirigir usando la misma lógica dinámica que el botón "Volver"
        this.redirectToUserRoute();
      });

      // Event listeners para grabación
      this.jitsiApi.addEventListener('recordingStatusChanged', (status: any) => {
        console.log("Estado de grabación cambió:", status);
        if (status.on) {
          console.log("Grabación iniciada");
        } else {
          console.log("Grabación detenida");
        }
      });

      this.jitsiApi.addEventListener('recordingLinkAvailable', (link: any) => {
        console.log("Enlace de grabación disponible:", link);
      });

      // Aplicar estilos personalizados para el logo después de que se cargue Jitsi Meet
      this.jitsiApi.addEventListener('videoConferenceJoined', () => {
        console.log("Te uniste a la conferencia");
        // Ocultar el header cuando se une a la conferencia
        this.showHeader = false;
        // Aplicar estilos personalizados para el logo
        setTimeout(() => {
          this.applyCustomLogoStyles();
        }, 2000);
        
        // Iniciar monitoreo periódico para ocultar recomendaciones móviles
        this.startMobileRecommendationsMonitoring();
      });

    } catch (error) {
      console.error('Error al inicializar Jitsi Meet:', error);
      this.error = 'Error al inicializar la videollamada';
    }
  }

  private hideMainLayoutElements(): void {
    // Ocultar elementos comunes del layout principal
    const elementsToHide = [
      'app-sidebar',
      '.sidebar',
      '.main-header',
      '.header',
      '.navbar',
      '.navigation',
      '.main-nav',
      'nav',
      '.top-bar',
      '.toolbar'
    ];

    elementsToHide.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element: any) => {
        if (element) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
        }
      });
    });

    // Ocultar el body principal y hacer que el componente ocupe toda la pantalla
    const body = document.body;
    if (body) {
      body.style.overflow = 'hidden';
      body.style.margin = '0';
      body.style.padding = '0';
    }

    // Ocultar el app-root si existe
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      (appRoot as HTMLElement).style.position = 'fixed';
      (appRoot as HTMLElement).style.top = '0';
      (appRoot as HTMLElement).style.left = '0';
      (appRoot as HTMLElement).style.width = '100vw';
      (appRoot as HTMLElement).style.height = '100vh';
      (appRoot as HTMLElement).style.zIndex = '9998';
    }
  }

  private showMainLayoutElements(): void {
    // Restaurar elementos del layout principal
    const elementsToShow = [
      'app-sidebar',
      '.sidebar',
      '.main-header',
      '.header',
      '.navbar',
      '.navigation',
      '.main-nav',
      'nav',
      '.top-bar',
      '.toolbar'
    ];

    elementsToShow.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element: any) => {
        if (element) {
          element.style.display = '';
          element.style.visibility = '';
        }
      });
    });

    // Restaurar el body
    const body = document.body;
    if (body) {
      body.style.overflow = '';
      body.style.margin = '';
      body.style.padding = '';
    }

    // Restaurar el app-root
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      (appRoot as HTMLElement).style.position = '';
      (appRoot as HTMLElement).style.top = '';
      (appRoot as HTMLElement).style.left = '';
      (appRoot as HTMLElement).style.width = '';
      (appRoot as HTMLElement).style.height = '';
      (appRoot as HTMLElement).style.zIndex = '';
    }
  }

  private applyCustomLogoStyles(): void {
    try {
      // Buscar el iframe de Jitsi Meet
      const jitsiIframe = document.querySelector('#jitsi-container iframe') as HTMLIFrameElement;
      if (jitsiIframe && jitsiIframe.contentDocument) {
        const iframeDoc = jitsiIframe.contentDocument;
        
        // Crear estilos CSS personalizados
        const customStyles = `
          .leftwatermark,
          .watermark,
          .brand-watermark,
          .main-logo,
          .logo {
            transform: scale(2.5) !important;
            transform-origin: top left !important;
          }
          
          .leftwatermark img,
          .watermark img,
          .brand-watermark img,
          .main-logo img,
          .logo img {
            width: 200px !important;
            height: 60px !important;
            max-width: 200px !important;
            max-height: 60px !important;
          }
          
          img[src*="logo-responsive.png"],
          img[src*="phoenix"] {
            width: 200px !important;
            height: 60px !important;
            max-width: 200px !important;
            max-height: 60px !important;
          }
          
          /* Ocultar recomendaciones móviles */
          .app-promotion,
          .mobile-app-promotion,
          .native-app-promotion,
          .browser-app-promotion,
          .app-store-promotion,
          .play-store-promotion,
          .download-app-promotion,
          .deep-linking-page,
          .app-download-section,
          .mobile-recommendations,
          .app-recommendations,
          .native-app-section,
          .browser-app-section,
          [class*="app-promotion"],
          [class*="mobile-app"],
          [class*="native-app"],
          [class*="browser-app"],
          [class*="download-app"],
          [class*="app-store"],
          [class*="play-store"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            height: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
          
          /* Ocultar botón "Terminar reunión para todos" */
          [data-testid="end-meeting-button"],
          [aria-label*="Terminar reunión para todos"],
          [aria-label*="End meeting for everyone"],
          .end-meeting-button,
          .end-meeting-for-all-button,
          .moderator-end-meeting-button,
          [class*="end-meeting"],
          [class*="terminate-meeting"],
          [class*="end-for-all"],
          [class*="terminate-for-all"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            height: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
        `;
        
        // Crear elemento style y agregarlo al iframe
        const styleElement = iframeDoc.createElement('style');
        styleElement.textContent = customStyles;
        iframeDoc.head.appendChild(styleElement);
        
        console.log('Estilos personalizados aplicados al logo y recomendaciones móviles de Jitsi Meet');
      }
    } catch (error) {
      console.log('No se pudieron aplicar estilos personalizados (normal en algunos navegadores por CORS):', error);
    }
  }

  private startMobileRecommendationsMonitoring(): void {
    // Limpiar cualquier intervalo existente
    if (this.mobileRecommendationsInterval) {
      clearInterval(this.mobileRecommendationsInterval);
    }
    
    // Monitorear cada 2 segundos para ocultar recomendaciones móviles
    this.mobileRecommendationsInterval = setInterval(() => {
      this.hideMobileRecommendations();
    }, 2000);
  }

  private hideMobileRecommendations(): void {
    try {
      // Buscar el iframe de Jitsi Meet
      const jitsiIframe = document.querySelector('#jitsi-container iframe') as HTMLIFrameElement;
      if (jitsiIframe && jitsiIframe.contentDocument) {
        const iframeDoc = jitsiIframe.contentDocument;
        
        // Selectores para elementos de recomendaciones móviles y botones de moderador
        const selectors = [
          '.app-promotion',
          '.mobile-app-promotion',
          '.native-app-promotion',
          '.browser-app-promotion',
          '.app-store-promotion',
          '.play-store-promotion',
          '.download-app-promotion',
          '.deep-linking-page',
          '.app-download-section',
          '.mobile-recommendations',
          '.app-recommendations',
          '.native-app-section',
          '.browser-app-section',
          '[class*="app-promotion"]',
          '[class*="mobile-app"]',
          '[class*="native-app"]',
          '[class*="browser-app"]',
          '[class*="download-app"]',
          '[class*="app-store"]',
          '[class*="play-store"]',
          // Selectores para botón "Terminar reunión para todos"
          '[data-testid="end-meeting-button"]',
          '[aria-label*="Terminar reunión para todos"]',
          '[aria-label*="End meeting for everyone"]',
          '.end-meeting-button',
          '.end-meeting-for-all-button',
          '.moderator-end-meeting-button',
          '[class*="end-meeting"]',
          '[class*="terminate-meeting"]',
          '[class*="end-for-all"]',
          '[class*="terminate-for-all"]'
        ];
        
        // Ocultar cada elemento encontrado
        selectors.forEach(selector => {
          const elements = iframeDoc.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element) {
              element.style.display = 'none';
              element.style.visibility = 'hidden';
              element.style.opacity = '0';
              element.style.height = '0';
              element.style.width = '0';
              element.style.overflow = 'hidden';
            }
          });
        });
      }
    } catch (error) {
      // Ignorar errores de CORS
    }
  }

  goBack(): void {
    this.redirectToUserRoute();
  }

  /**
   * Redirige al usuario a su ruta correspondiente basándose en su tipo y estado
   */
  private redirectToUserRoute(): void {
    const currentUser: User | null = this.user || this.authService.getUser();

    if (!currentUser) {
      this.router.navigate(['/auth/login']);
      return;
    }

    const route = this.authService.getUserRedirectRoute(currentUser);
    this.router.navigate([route]);
  }

  getMeetingTitle(): string {
    if (!this.appointmentData) return 'Reunión Phoenix';
    
    const coachType = this.appointmentData.coach.type.name || 'Experto';
    return `${coachType} ${this.appointmentData.coach.name}`;
  }

  getMeetingTime(): string {
    if (!this.appointmentData) return '';
    
    const date = new Date(`${this.appointmentData.date} ${this.appointmentData.time}`);
    return date.toLocaleString('es-PE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Carga los datos del usuario
   */
  private loadUserData(): void {
    this.user = this.authService.getUser();
  }

  /**
   * Verifica si el usuario es Executive
   */
  isExecutiveUser(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 && // Customer
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  /**
   * Obtiene la ruta correcta para el dashboard
   */
  getDashboardRoute(): string {
    return this.isExecutiveUser() ? '/dashboard/dashboard-executive' : '/dashboard/dashboard-menu';
  }
}
